import React, {
	useEffect,
	useState,
} from 'react';
import ListingConfig from './ListingConfig.js';

import style from './AccountSettings.module.css';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import TextSmall from 'Vulture/Field/TextSmall.js';
import {
	getFDValue,
} from 'Vulture/Helpers.js';
import {
	buildTestData,
} from 'Vulture/TestData.js';
import ArrayOptions from './ArrayOptions.js';
import MessagingOptions from './MessagingOptions.js';
import {
	deleteAccount, fetchDataSets,
} from 'Vulture/Account.js';

import {
	generateConcatenatedColumns,
} from 'Vulture/Content';

const DeleteInputField = (props) => {
	const {
		setConfirmModalContent,
	} = props;

	const [ deleteField, setDeleteField, ] = useState('');
	const [ deleteConfirmed, setDeleteConfirmed, ] = useState(false);

	return (
		<>
			<input
				value={deleteField}
				onChange={(e) => {
					if (e.target.value === 'DELETE') {
						setDeleteConfirmed(true);
					} else {
						setDeleteConfirmed(false);
					}
					setDeleteField(e.target.value);
				}}

			/>
			<button
				className='button'
				onClick={(e) => {
					e.preventDefault();
					setConfirmModalContent('');
				}}
			>
				Cancel
			</button>
			<button
				type='submit'
				className={`button ${deleteConfirmed ? '' : 'button-disabled no-click'}`}
			>
				DELETE
			</button>
		</>
	);
};

const AccountSettingsView = (props) => {
	const {
		app,
		accountUUID,
		fd,
		listingConfigMap,
		pendingChanges,
		settingMap,
	} = props;

	const [ confirmModalContent, setConfirmModalContent, ] = useState('');
	const [ dataSets, setDataSets, ] = useState({});
	const [ messageLabels, setMessageLabels, ] = useState([]);
	const [ isLoading, setIsLoading, ] = useState(false);
	const [ activeTab, setActiveTab, ] = useState('basic');

	// Get initial tab from URL hash or default to 'basic'
	const getInitialTab = () => {
		const hash = window.location.hash.substring(1); // Remove the # character
		const validTabs = [ 'basic', 'security', 'arrays', 'messaging', 'listings', ];
		return validTabs.includes(hash) ? hash : 'basic';
	};

	// Initialize active tab from URL
	useEffect(() => {
		setActiveTab(getInitialTab());
	}, []);

	// Handle tab changes and update URL
	const handleTabChange = (tabId) => {
		setActiveTab(tabId);
		// Update URL hash without triggering a page reload
		window.history.replaceState(null, null, `#${tabId}`);
	};

	// Listen for hash changes (back/forward navigation)
	useEffect(() => {
		const handleHashChange = () => {
			setActiveTab(getInitialTab());
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => {
			return window.removeEventListener('hashchange', handleHashChange);
		};
	}, []);

	// Tab configuration
	const tabs = [
		{
			id:         'basic',
			label:      'Basic Information',
			hasChanges: () => {
				return hasBasicInfoChanges();
			},
		},
		{
			id:         'security',
			label:      'Security & Access',
			hasChanges: () => {
				return hasSecurityChanges();
			},
		},
		{
			id:         'arrays',
			label:      'Array Options',
			hasChanges: () => {
				return hasArrayOptionsChanges();
			},
		},
		{
			id:         'messaging',
			label:      'Messaging',
			hasChanges: () => {
				return hasMessagingChanges();
			},
		},
		{
			id:         'listings',
			label:      'Listing Config',
			hasChanges: () => {
				return hasListingConfigChanges();
			},
		},
	];

	// Helper function to check if a section has pending changes
	const hasSectionChanges = (sectionKeys) => {
		if (!pendingChanges) return false;
		return sectionKeys.some((key) => {
			return Object.keys(pendingChanges).some((pendingKey) => {
				return pendingKey.includes(key);
			});
		}
		);
	};

	// Section-specific change detection
	const hasBasicInfoChanges = () => {
		return hasSectionChanges([
			'display_name', 'salesforce_id_val',
		]);
	};

	const hasSecurityChanges = () => {
		return hasSectionChanges([
			'require_2fa', 'allow_slideshow_url', 'show_billing',
		]);
	};

	const hasArrayOptionsChanges = () => {
		return hasSectionChanges([
			'array_options',
		]);
	};

	const hasMessagingChanges = () => {
		return hasSectionChanges([
			'message_labels', 'notification_settings',
		]);
	};

	const hasListingConfigChanges = () => {
		return hasSectionChanges([
			'listing_config', 'column_metadata',
		]);
	};


	useEffect(() => {
		const loadData = async () => {
			setIsLoading(true);
			try {
				await app.fetchAccount(accountUUID);
			} catch (error) {
				console.error('Error loading account data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		app.load(loadData);
		fetchDataSets(setDataSets);
	}, [ accountUUID, app, ]);

	// Enable natural page scrolling when component mounts
	useEffect(() => {
		// Store original overflow value
		const originalHtmlOverflow = document.documentElement.style.overflow;
		const originalBodyOverflow = document.body.style.overflow;

		// Enable scrolling
		document.documentElement.style.overflow = 'auto';
		document.body.style.overflow = 'auto';

		// Cleanup: restore original values when component unmounts
		return () => {
			document.documentElement.style.overflow = originalHtmlOverflow;
			document.body.style.overflow = originalBodyOverflow;
		};
	}, []);

	// Early return with loading state if no data yet
	if (Object.keys(fd).length === 0) {
		return (
			<div className={style.pageContainer}>
				<div className={style.loadingOverlay}>
					<div className={style.loadingSpinner}>
						<div className={style.spinner} />
						<p>Loading account settings...</p>
					</div>
				</div>
			</div>
		);
	}

	if (!fd[`account.${accountUUID}`]) {
		return (
			<div className={style.pageContainer}>
				<div className={style.pageContent}>
					<div className={style.pageHeader}>
						<h2>Account Settings</h2>
					</div>
					<div style={{
						textAlign: 'center',
						padding:   '2rem',
						color:     '#6c757d',
					}}>
						<p>Account not found or access denied.</p>
					</div>
				</div>
			</div>
		);
	}

	const displayName = getFDValue(fd, `account.${accountUUID}.display_name`);
	const salesforceIDVal = getFDValue(fd, `account.${accountUUID}.salesforce_id_val`);

	const handleDeleteAccount = async () => {
		const data = await deleteAccount(fd, accountUUID);

		if (data?.err) {
			alert('Please make sure the accountUUID is not associated to any other users.');
		} else if (data) {
			app.redirect({
				to:     '/accounts',
				reload: true,
			});
		}
	};

	const handleMessageLabelsUpdate = (labels) => {
		setMessageLabels(labels);
	};

	const deleteAccountPreflightCheck = () => {
		const children = [];
		const childrenTexts = [];

		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (m1Key === `account.${accountUUID}`) {
				for (const m2Key of Object.keys(m1Data)) {
					if (m2Key.split('.').length === 2) {
						children.push(m2Key);
					}
				}
			}
		}

		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (children.includes(m1Key)) {
				for (const [ m2Key, m2Data, ] of Object.entries(m1Data)) {
					if (m2Key === 'display_name') {
						childrenTexts.push(`${m1Key}: ${m2Data}`);
					}
				}
			}
		}

		showCannotDeleteModal(childrenTexts);
	};

	const showCannotDeleteModal = (children) => {
		setConfirmModalContent(
			<div className="confirm-modal-container">
				<div className="confirm-modal-content">
					<div>
						<p>By hitting DELETE you will be deleting all of the following child components.  These cannot be recovered.  Enter the word “DELETE” below to continue or Cancel to exit.</p>
						{children.map((child, i) => {
							return <p key={i}>{child.split(': ')[1]}</p>;
						})}
						<form onSubmit={(e) => {
							e.preventDefault();
							handleDeleteAccount();
						}}>
							<div className="confirm-modal-buttons">
								<DeleteInputField setConfirmModalContent={setConfirmModalContent}/>
							</div>
						</form>
					</div>
				</div>
			</div>
		);
	};

	const hasSaveBlocks = () => {
		const saveBlocks = [];

		const pendingArrayOptionsChanges = pendingChanges?.[`account.${accountUUID}.array_options`];

		if (pendingArrayOptionsChanges) {
			const parsedJson = JSON.parse(pendingArrayOptionsChanges);

			Object.entries(parsedJson).forEach(([ dataName, arrayOptionsSet, ]) => {
				if (!Array.isArray(arrayOptionsSet?.options)) {
					saveBlocks.push(`- ${dataName}'s options must be an array`);
					return;
				}

				arrayOptionsSet.options.forEach((option) => {
					if (!(typeof option === 'object' && !Array.isArray(option) && option !== null)) {
						saveBlocks.push(`- ${dataName}'s options must be objects`);
						return;
					}

					if (typeof option.label !== 'string') {
						saveBlocks.push(`- ${dataName}'s labels must be strings`);
						return;
					}

					switch (arrayOptionsSet.type) {
					case 'string':
					case 'text':
						if (!(typeof option.value === 'string')) {
							saveBlocks.push(`- ${dataName}'s values must be strings`);
							return;
						}
						break;
					case 'float':
						if (!(typeof option.value === 'number')) {
							saveBlocks.push(`- ${dataName}'s values must be floats`);
							return;
						}
						break;
					case 'int':
						if (!(typeof option.value === 'number') || option.value !== Math.floor(option.value)) {
							saveBlocks.push(`- ${dataName}'s values must be integers`);
							return;
						}
						break;
					case 'bool':
						if (!(typeof option.value === 'boolean')) {
							saveBlocks.push(`- ${dataName}'s values must be booleans`);
							return;
						}
						break;

					default:
						console.log(`The type ${arrayOptionsSet.type} has no validation logic implemented.`);
					}
				});
			});
		}

		return saveBlocks;
	};

	let saveButtonClass = 'button button-disabled button-save-lg no-click';
	if (app.hasPendingChanges('account_settings')) {
		saveButtonClass = 'button button-save-lg';
	}

	if (salesforceIDVal === '' || displayName === '') {
		saveButtonClass = 'button button-disabled button-save-lg no-click';
	}

	return (
		<div className={style.pageContainer}>
			{confirmModalContent}
			{isLoading && (
				<div className={style.loadingOverlay}>
					<div className={style.loadingSpinner}>
						<div className={style.spinner} />
						<p>Loading account settings...</p>
					</div>
				</div>
			)}
			<div className={style.pageContent}>
				<div className={style.pageHeader}>
					<h2>Account Settings</h2>
					<div className={style.buttons}>
						<button
							className='button button-save-lg alert-button'
							onClick={(e) => {
								deleteAccountPreflightCheck();
								// TODO: /w/delete/m1/m1ID
								// TODO: handle error(has_children)
								// TODO: handle success(no_children)

								// eslint-disable-next-line no-debugger
								debugger;
							}}
							data-testing-info={buildTestData('button--delete')}
							disabled={isLoading}
						>
							<CustomIcon icon='trash' />Delete Account
						</button>
						<button
							onClick={(e) => {
								const saveBlocks = hasSaveBlocks();

								if (saveBlocks.length === 0) {
									setIsLoading(true);
									app.saveData('account_settings', () => {
										for (const pendingKey of Object.keys(pendingChanges)) {
											if (pendingKey.startsWith('listing_config.') && pendingKey.endsWith('.column_metadata')) {
												const listingConfigUUID = pendingKey.split('.')[1];
												generateConcatenatedColumns(listingConfigUUID);
											}
										}
										setIsLoading(false);
									});
								} else {
									const saveBlockMessage = saveBlocks.join('\n');
									alert(saveBlockMessage);
								}

							}}
							className={saveButtonClass}
							data-testing-info={buildTestData('button--save')}
							disabled={isLoading}
						>
							<CustomIcon icon='save' /> {isLoading ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>

				<div className={style.accountContent}>
					{/* Tab Navigation */}
					<div className={style.tabNavigation}>
						{tabs.map((tab) => {
							return (
								<button
									key={tab.id}
									className={`${style.tabButton} ${activeTab === tab.id ? style.tabButtonActive : ''} ${tab.hasChanges() ? style.tabButtonHasChanges : ''}`}
									onClick={() => {
										return handleTabChange(tab.id);
									}}
								>
									{tab.label}
									{tab.hasChanges() && <span className={style.changeIndicator}>•</span>}
								</button>
							);
						})}
					</div>

					{/* Tab Content */}
					<div className={style.tabContent}>
						{/* Basic Information Tab */}
						{activeTab === 'basic' && (
							<div className={style.tabPanel}>
								<div className={style.settingsGrid}>
									<div className={style.column1}>
										<div className={style.settingsField}>
											<div
												className={style.sectionLabel}
												onClick={(e) => {
													e.preventDefault();
													let url = `https://touchsource.lightning.force.com/lightning/r/Account/${salesforceIDVal}/view`;
													window.open(url, '_blank').focus();
												}}
											>
												<CustomIcon icon='external-link-alt' />
												Salesforce Integration
											</div>
											<TextSmall
												value={salesforceIDVal}
												setValue={app.setValue.bind(app, 'account_settings', `account.${accountUUID}.salesforce_id_val`)}
												dataTestingInfo={buildTestData('input--salesforce')}
											/>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.settingsField}>
											<label className={style.label}>Display Name</label>
											<TextSmall
												value={displayName}
												setValue={app.setValue.bind(app, 'account_settings', `account.${accountUUID}.display_name`)}
												dataTestingInfo={buildTestData('input--details')}
											/>
										</div>
										<div className={style.settingsField}>
											<label className={style.label}>Account UUID</label>
											<input
												disabled={true}
												value={accountUUID}
												data-testing-info={buildTestData('input--uuid')}
											/>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Security & Access Settings Tab */}
						{activeTab === 'security' && (
							<div className={style.tabPanel}>
								<div className={style.settingsGrid}>
									<div className={style.column2}>
										<div className={style.checkboxContainer}>
											<div className={style.checkboxLabel}>Two Factor Authentication</div>
											<div className={style.checkbox}>
												<div
													className={getFDValue(fd, `account.${accountUUID}.require_2fa`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.require_2fa`);
														const keys = [
															`account.${accountUUID}.require_2fa`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer}>
											<div className={style.checkboxLabel}>Allow Slideshow URLs</div>
											<div className={style.checkbox}>
												<div
													className={getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`);
														const keys = [
															`account.${accountUUID}.allow_slideshow_url`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer}>
											<div className={style.checkboxLabel}>Show Billing Information</div>
											<div className={style.checkbox}>
												<div
													className={getFDValue(fd, `account.${accountUUID}.show_billing`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.show_billing`);
														const keys = [
															`account.${accountUUID}.show_billing`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Array Options Tab */}
						{activeTab === 'arrays' && (
							<div className={style.tabPanel}>
								<ArrayOptions
									app={app}
									accountUUID={accountUUID}
									fd={fd}
									pendingChanges={pendingChanges}
									settingMap={settingMap}
								/>
							</div>
						)}

						{/* Messaging Options Tab */}
						{activeTab === 'messaging' && (
							<div className={style.tabPanel}>
								<MessagingOptions
									app={app}
									accountUUID={accountUUID}
									fd={fd}
									pendingChanges={pendingChanges}
									settingMap={settingMap}
									onMessageLabelsUpdate={handleMessageLabelsUpdate}
								/>
							</div>
						)}

						{/* Listing Configuration Tab */}
						{activeTab === 'listings' && (
							<div className={style.tabPanel}>
								<ListingConfig
									app={app}
									accountUUID={accountUUID}
									fd={fd}
									dataSets={dataSets}
									listingConfigMap={listingConfigMap}
									pendingChanges={pendingChanges}
									settingMap={settingMap}
									messageLabels={messageLabels}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default AccountSettingsView;